import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  afterNextRender,
  booleanAttribute,
  inject,
} from '@angular/core';

/** Below this the box would be too short to be worth scrolling inside. */
const MIN_BOX_HEIGHT = 240;

/**
 * Keeps a table's header row in view while its rows scroll underneath. Put it on
 * the <table>: `<table appStickyTableHead>`, or `[appStickyTableHead]="false"`.
 *
 * The header cells use native `position: sticky` (styles in the root styles.css,
 * `table[data-sticky-head]`, in @layer base so a cell's own Tailwind classes such
 * as pinned `sticky right-0 z-20 bg-slate50` still win). Native sticky is the only
 * way to keep it perfectly still: the browser scrolls on its own thread, so a
 * header moved from script always lags a frame and visibly jumps.
 *
 * Tables sit in an `overflow-x-auto` box for sideways scrolling, and that box is
 * what a sticky header sticks to, not the page. So the box is made the vertical
 * scroller too. Its height is the page's visible height minus everything below the
 * table (pagination, padding), so at the page's furthest scroll the box sits
 * exactly in view: the page can never carry the header off the top, and the rows
 * scroll inside the box under it. Tables with no such box stick to the page
 * directly.
 *
 * Only sizing and a shadow toggle run from script, outside the Angular zone, and
 * never per scroll frame.
 */
@Directive({
  selector: 'table[appStickyTableHead]',
  standalone: true,
  host: {
    '[attr.data-sticky-head]': 'enabled ? "" : null',
  },
})
export class StickyTableHeadDirective implements OnChanges, OnDestroy {
  @Input({ alias: 'appStickyTableHead', transform: booleanAttribute }) enabled = true;

  private readonly table = inject<ElementRef<HTMLTableElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);

  /** The table's sideways scroller, which also scrolls it vertically; null when there is none. */
  private box: HTMLElement | null = null;
  /** What scrolls the page around the table; found once it actually overflows. */
  private pageScroller: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private frame = 0;
  private detach: (() => void) | null = null;

  constructor() {
    afterNextRender(() => this.start());
  }

  ngOnChanges(): void {
    if (this.detach) {
      this.scheduleSize();
    }
  }

  ngOnDestroy(): void {
    this.detach?.();
    this.detach = null;
  }

  private readonly scheduleSize = (): void => {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.sizeBox();
    });
  };

  private start(): void {
    this.box = this.findSidewaysScroller();
    // Header cells often have no background of their own (it's on the row or
    // <thead>); stuck over scrolling rows they need one, or the rows show through.
    this.table.style.setProperty('--sticky-head-bg', this.headBackground());

    const box = this.box;
    const onBoxScroll = () => this.table.toggleAttribute('data-sticky-head-stuck', !!box && box.scrollTop > 0);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.scheduleSize, { passive: true });
      box?.addEventListener('scroll', onBoxScroll, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        // Rows loading and anything below the table changing size move the numbers.
        this.resizeObserver = new ResizeObserver(this.scheduleSize);
        this.resizeObserver.observe(this.table);
      }
    });
    this.sizeBox();

    this.detach = () => {
      window.removeEventListener('resize', this.scheduleSize);
      box?.removeEventListener('scroll', onBoxScroll);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
      this.clearBox();
    };
  }

  private sizeBox(): void {
    const box = this.box;
    if (!box) {
      return;
    }
    if (!this.enabled) {
      this.clearBox();
      return;
    }

    if (!this.pageScroller) {
      // Look for it with the box uncapped, so a cap from earlier can't hide the overflow.
      this.clearBox();
      const found = this.findPageScroller(box);
      const documentScroller = document.scrollingElement as HTMLElement | null;
      if (found) {
        this.pageScroller = found;
        // Content below the table (pagination, other sections) changes the cap too.
        Array.from(found.children).forEach((child) => this.resizeObserver?.observe(child));
      } else if (documentScroller && documentScroller.scrollHeight > window.innerHeight + 1) {
        this.pageScroller = documentScroller;
      } else {
        // Nothing scrolls yet (rows still loading, or a short table): no cap needed.
        // The table growing triggers another look.
        return;
      }
    }
    const scroller = this.pageScroller;

    // Everything between the box's bottom and the end of the scrolled content.
    const isDocument = scroller === document.scrollingElement;
    const visibleHeight = isDocument ? window.innerHeight : scroller.clientHeight;
    const contentTop = isDocument
      ? -scroller.scrollTop
      : scroller.getBoundingClientRect().top + scroller.clientTop - scroller.scrollTop;
    const belowBox = contentTop + scroller.scrollHeight - box.getBoundingClientRect().bottom;

    box.style.maxHeight = `${Math.max(MIN_BOX_HEIGHT, Math.floor(visibleHeight - belowBox))}px`;
    box.style.overflowY = 'auto';
  }

  private clearBox(): void {
    if (this.box) {
      this.box.style.maxHeight = '';
      this.box.style.overflowY = '';
    }
    this.table.removeAttribute('data-sticky-head-stuck');
  }

  private findSidewaysScroller(): HTMLElement | null {
    for (let node = this.table.parentElement; node && node !== document.body; node = node.parentElement) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') {
        return node;
      }
    }
    return null;
  }

  /**
   * The nearest ancestor that really scrolls vertically: the shell's <main>, a popup
   * body. overflow-x:auto makes overflow-y compute to auto as well, so wrappers that
   * only scroll sideways look the same; they never overflow vertically, which is
   * what tells them apart. Null until something overflows (e.g. rows still loading),
   * and when the window itself scrolls.
   */
  private findPageScroller(box: HTMLElement): HTMLElement | null {
    for (let node = box.parentElement; node && node !== document.body; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
    }
    return null;
  }

  /** The first solid background behind the header row: the row, <thead>, or whatever holds the table. */
  private headBackground(): string {
    const head = this.table.tHead;
    for (let node: Element | null = head?.rows[0] ?? head ?? this.table; node; node = node.parentElement) {
      const color = getComputedStyle(node).backgroundColor;
      if (color && color !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(color)) {
        return color;
      }
    }
    return '#fff';
  }
}
